import { useAppSelector } from 'reduxTyped'
import { usePlansViewContext } from 'common/billing/components/Paywall/variants/UniversalPaywall/steps/Plan/PlansViewContext'
import { BillingCurrency } from 'common/billing/constants/BillingCurrency'
import { PricePeriod } from 'common/billing/constants/PricePeriod'
import { ProductSlug } from 'common/billing/constants/ProductSlug'
import {
  getMigrationData,
  isActiveProMigration,
} from 'common/billing/entities/billingVersions/selectors'
import { getCurrency } from 'common/billing/entities/pro/utils/convertPrice'
import { ProductSlugToTypeMap } from 'common/billing/entities/pro/utils/ProductSlugToTypeMap'
import { isWebProductSlug } from 'common/billing/helpers/typeguards'
import { useCurrentSubscription } from 'common/billing/hooks/useCurrentSubscription'
import { useMigrationConsent } from 'common/billing/hooks/useMigrationConsent'
import { ProductType } from 'common/core/interfaces/products'
import { FlexToV3MigrationState } from 'shared/api/requests/billing/schemas'
import { MigrationConsentPlanOffer } from 'shared/api/requests/billingFlex/schemas'

import { calculateAnnualSavings } from './utils'

interface TargetPlanData {
  slug: ProductSlug
  type: ProductType.ESSENTIAL | ProductType.PRO | ProductType.BUSINESS | ProductType.ADVANCED
  planName: string
  activeContacts: number
  overagePrice: {
    amount: number
    currency: BillingCurrency
  }
  price: {
    amount: number
    period: PricePeriod
    currency: BillingCurrency
  }
}

export interface MigrationPlansViewReady {
  isReady: true
  targetData: TargetPlanData
  isPriceDown: boolean
  activeContacts: number
  registerConsent: (slug: ProductSlug) => void
}

export type MigrationPlansView = { isReady: false } | MigrationPlansViewReady

/**
 * Resolves the PRO Flex → V3 migration offer into the plain data the paywall needs — the target
 * plan (slug, name, price and active contacts) and whether it costs less than the current plan.
 * Every input comes from redux, `PlansViewContext` or the current subscription.
 * `ProMigrationPlansView` calls it once and passes the result down as `migrationViewData`, so the
 * price-up and price-down views (and the cards they render) read the same snapshot instead of
 * each subscribing again.
 *
 * The migration payload's target slug doesn't follow the period toggle, so the target tier is
 * matched against `paidPlanCards` — which is rebuilt per period — and the card wins over the
 * payload for slug, name, price and contacts whenever it is found.
 * Note: This is due to known issue in segmentation.
 *
 * Returns `{ isReady: false }` when there is no scheduled migration to show (none active, the
 * account is cancelling, or the target product is missing from the payload); callers must
 * render their own fallback in that case.
 */
export const useMigrationPlansView = (): MigrationPlansView => {
  const currentSubscription = useCurrentSubscription()
  const { total } = currentSubscription || {}

  const migrationData = useAppSelector(getMigrationData)
  const { paidPlanCards } = usePlansViewContext()
  const [, mutate] = useMigrationConsent()
  const currency = getCurrency()

  const activeMigration = isActiveProMigration(migrationData) ? migrationData.data : null
  const isScheduledOrWaitingConsent =
    activeMigration?.state === FlexToV3MigrationState.SCHEDULED ||
    activeMigration?.state === FlexToV3MigrationState.WAITING_CONSENT
  const { targetPlan } = activeMigration || {}
  const isWaitingConsent = activeMigration?.state === FlexToV3MigrationState.WAITING_CONSENT

  if (!isScheduledOrWaitingConsent || !targetPlan || activeMigration.isCancelling) {
    return { isReady: false }
  }

  const targetProduct = activeMigration.products[targetPlan]

  if (!targetProduct) {
    return { isReady: false }
  }

  // `paidPlanCards` is rebuilt for the selected period, so take the target tier's card from there.
  const targetCard = paidPlanCards.find(
    (card) => ProductSlugToTypeMap[card.slug] === targetProduct.type,
  )

  const targetSlug = targetCard?.slug ?? targetProduct.slug

  const targetPrice = targetCard
    ? { amount: targetCard.price.amount, period: PricePeriod.MONTHLY }
    : {
        amount: targetProduct.price?.amount || 0,
        period: targetProduct.price?.period,
      }

  const isPriceDown =
    calculateAnnualSavings({
      currentPlanPrice: total?.amount || 0,
      targetPlanPrice: targetProduct.price?.amount || 0,
      targetPlanPeriod: targetProduct.price?.period,
      currency: total?.currency || currency,
    }).amount > 0

  const registerConsent = (slug: ProductSlug, shownPlans?: MigrationConsentPlanOffer[]) => {
    if (isWaitingConsent && isWebProductSlug(slug) && isWebProductSlug(targetSlug)) {
      return mutate({
        plan: slug,
        consentChannel: 'in-app',
        status: 'accepted',
        metadata: {
          noticeVersion: '2026-07-01',
          recommendedPlan: targetSlug,

          recommendedPrice: {
            amount: targetPrice.amount,
            currency: targetProduct.price?.currency || currency,
          },
          shownPlans: shownPlans ?? [
            {
              plan: targetSlug,
              price: {
                amount: targetPrice.amount,
                currency: targetProduct.price?.currency || currency,
              },
            },
          ],
        },
      })
    }
  }

  return {
    isReady: true,
    isPriceDown,
    targetData: {
      slug: targetSlug,
      planName: targetCard?.title ?? targetProduct.name,
      price: { ...targetPrice, currency: targetProduct.price?.currency || currency },
      activeContacts: targetCard?.contacts ?? targetProduct.limit_touched_contacts ?? 0,
      type: targetProduct.type,
      overagePrice: {
        amount: targetProduct.overage_price?.amount || 0,
        currency: targetProduct.overage_price?.currency || currency,
      },
    },
    activeContacts: activeMigration.activeContacts,
    registerConsent,
  }
}
